import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Zap } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact "credits remaining" pill for the AppShell header. Reads the same
// monthly/daily usage bucket as the Generator meter and shows how many
// generations the user has left. Admins see "Unlimited".
const CreditsBadge = () => {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const fetchUsage = () => {
      axios
        .get(`${API}/api/auth/daily-usage`, { headers: getAuthHeader() })
        .then((res) => { if (!cancelled) setUsage(res.data); })
        .catch(() => { if (!cancelled) setUsage(null); });
    };
    fetchUsage();
    // Refetch when any page signals a generation was used, so the header
    // count updates without a full reload.
    window.addEventListener('usage-updated', fetchUsage);
    return () => {
      cancelled = true;
      window.removeEventListener('usage-updated', fetchUsage);
    };
  }, [isAuthenticated, getAuthHeader]);

  if (!usage) return null;

  if (usage.plan === 'admin' || !usage.limit) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border border-[#E6DED1] bg-[#FBF7F1] px-3 py-1 text-xs text-[#5E574E]"
        data-testid="credits-badge"
      >
        <Zap className="w-3.5 h-3.5 text-[#BE185D]" strokeWidth={1.8} />
        Unlimited credits
      </span>
    );
  }

  const remaining = Math.max(0, (usage.limit || 0) - (usage.count || 0));
  const isDaily = usage.period_type === 'daily';
  const low = remaining <= Math.max(1, Math.round((usage.limit || 0) * 0.15));

  return (
    <Link
      to="/subscription"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#E6DED1] bg-[#FBF7F1] px-3 py-1 text-xs text-[#5E574E] transition hover:border-[#E2D9CB] hover:text-[#1F1B16]"
      data-testid="credits-badge"
    >
      <Zap className={`w-3.5 h-3.5 ${low ? 'text-[#BE185D]' : 'text-[#9D174D]'}`} strokeWidth={1.8} />
      <span className={low ? 'text-[#BE185D] font-medium' : ''} data-testid="credits-badge-count">
        {remaining}
      </span>
      <span className="text-[#6F675D]">
        of {usage.limit} credits left {isDaily ? 'today' : 'this month'}
      </span>
    </Link>
  );
};

export default CreditsBadge;
